import { useFinalidades, usePessoas, useSistemas } from '../../../api/queries/referencias'
import { useSessao } from '../../../auth/contexto'
import { EtiquetaDePrioridade } from '../../../components/Etiqueta'
import { ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import {
  NATUREZA_DA_SOLUCAO,
  ROTULO_CAUSA_EMERGENCIAL,
  ROTULO_NATUREZA_DA_SOLUCAO,
  ROTULO_TIPO,
} from '../../../lib/glossario'
import type { ItemDeReferencia } from '../../../types/gmud'
import { ROTULO_DO_CHAMADO } from './pendencias'
import type { FormularioNovaGmud } from './esquema'

/**
 * O rótulo de um item de referência a partir do que o `<select>` guardou.
 *
 * O `react-hook-form` guarda o VALOR DO CAMPO, que num `<select>` é sempre texto — quem converte
 * para número é o `z.coerce` do esquema, e só na hora de validar. Comparar direto com `item.id`
 * daria falso em todos os casos, e a conferência mostraria "Não informado" para um sistema que a
 * pessoa escolheu.
 */
function rotuloDoItem(itens: ItemDeReferencia[] | undefined, escolhido: number | undefined): string {
  return itens?.find((item) => item.id === Number(escolhido))?.rotulo ?? ''
}

/**
 * A classificação, relida.
 *
 * Os valores vêm crus do formulário e podem estar todos vazios: quem recarrega a página em
 * `?etapa=3` volta com o preenchimento perdido — o rascunho só existe no servidor depois de salvo.
 * `ListaDeDados` já escreve "Não informado" onde falta, o que é a informação certa nessa hora.
 *
 * @param props o que foi preenchido até aqui
 * @returns a lista de conferência da etapa 1
 */
export function ResumoDaClassificacao({ valores }: { valores: FormularioNovaGmud }) {
  const { data: sistemas } = useSistemas()
  const { data: finalidades } = useFinalidades()
  const { data: pessoas } = usePessoas()
  const { usuario } = useSessao()

  const emergencial = valores.prioridade === 'EMERGENCIAL'

  const dados: Dado[] = [
    { rotulo: 'De onde vem a mudança', valor: ROTULO_TIPO[valores.tipo] ?? '' },
    {
      rotulo: 'Prioridade',
      valor: valores.prioridade ? <EtiquetaDePrioridade prioridade={valores.prioridade} /> : '',
    },
    { rotulo: ROTULO_DO_CHAMADO, valor: valores.chamadoNumero ?? '' },
    { rotulo: NATUREZA_DA_SOLUCAO.rotulo, valor: ROTULO_NATUREZA_DA_SOLUCAO[valores.naturezaDaSolucao] ?? '' },
    { rotulo: 'Sistema afetado', valor: rotuloDoItem(sistemas, valores.sistemaId) },
    { rotulo: 'Tipo de manutenção', valor: rotuloDoItem(finalidades, valores.finalidadeId) },
    { rotulo: 'Módulo', valor: valores.modulo ?? '' },
    { rotulo: 'Sub-módulo', valor: valores.subModulo ?? '' },
    {
      rotulo: 'Responsável pela execução',
      // O select guarda o id da pessoa (era 'EU' quando só havia duas opções); o nome sai da lista
      // de pessoas, ou do próprio usuário — a conferência dizia "Não informado" para quem escolheu.
      valor: nomeDoResponsavel(valores.responsavel, usuario?.id, usuario?.nome, pessoas),
    },
  ]

  // Os dois campos da emergência só existem quando a prioridade é emergencial — e é justamente
  // por isso que aparecem aqui: são o que o relatório mensal de fiscalização (TR 8.31.5.2) lê, e
  // é a última chance de conferir a causa declarada antes de ela virar registro.
  if (emergencial) {
    dados.splice(2, 0, {
      rotulo: 'Causa da emergência',
      valor: ROTULO_CAUSA_EMERGENCIAL[valores.causaEmergencial] ?? '',
    })
    dados.splice(3, 0, { rotulo: 'Por que não pode esperar', valor: valores.justificativaEmergencial ?? '' })
  }

  return <ListaDeDados dados={dados} />
}

/** O nome de quem vai executar, a partir do que o select guardou: o id da pessoa, ou 'EU' em rascunho antigo. */
function nomeDoResponsavel(
  valor: string | null | undefined,
  usuarioId: number | undefined,
  usuarioNome: string | undefined,
  pessoas: { id: number; nome: string }[] | undefined,
): string {
  if (!valor) {
    return ''
  }
  if (valor === 'EU' || (usuarioId !== undefined && valor === String(usuarioId))) {
    return usuarioNome ?? ''
  }
  return pessoas?.find((pessoa) => String(pessoa.id) === valor)?.nome ?? ''
}
